import buffer from '@turf/buffer'
import combine from '@turf/combine'
import { featureCollection } from '@turf/helpers'
import get from 'lodash/get'
import isNil from 'lodash/isNil'
import OLFeature from 'ol/Feature'

import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import GetTitleId from 'config/GetTitleId'
import PoziMap from 'map/map'
import selectVectorFeature from 'map/SelectVectorFeature'
import PoziNotifications from 'utils/notifications'
import { currentTargetLayerClear, currentTargetLayerShowGeom } from 'utils/target'
import { convertFeatureCollectionFeaturesToPolygons, featureCollectionIsPointsOnly } from 'utils/TurfUtils'
import WKTToGeoJSON from 'utils/WKTToGeoJSON'

export const WHATS_HERE_LAYER_TITLEID = 'whatshere'
export const FEATURE_HIGHLIGHT_COLOR = '#efa265'

/**
 * Return the Pozi config object for the WhatsHere layer.
 */
export const whatsHereLayerConfig = {
  config: {
    spatial: {
      id: 'id',
      label: 'label',
      loader: 'none',
      url: ''
    }
  },
  group: 'PoziGeneral',
  queryable: false,
  showInLayerControl: false,
  styleGeoJSONIO: {
    fillOpacity: 0,
    markerColorFillHex: FEATURE_HIGHLIGHT_COLOR,
    markerColorHex: FEATURE_HIGHLIGHT_COLOR,
    markerFillOpacity: 0.3,
    markerSize: 16,
    markerStrokeOpacity: 0.7,
    markerStrokeWidth: 6,
    strokeHex: FEATURE_HIGHLIGHT_COLOR,
    strokeOpacity: 0.6,
    strokeWidth: 12
  },
  title: WHATS_HERE_LAYER_TITLEID,
  titleid: WHATS_HERE_LAYER_TITLEID,
  type: 'GeoJSON',
  visible: true
}

/**
 * Clear all geometry from the whatshere layer.
 */
export function WhatsHereClear () {
  // console.debug('WhatsHereClear')

  const olWhatsHereLayerSource = PoziUtils.getOpenLayersObjectFromTitleID(WHATS_HERE_LAYER_TITLEID)?.getSource()
  if (olWhatsHereLayerSource) {
    olWhatsHereLayerSource.clear()
  } else {
    console.debug('WhatsHereClear - no Whats Here layer or source. This should not happen')
  }
}

/**
 * Show the feature geometry on the WhatsHere layer
 *
 * @param {GeoJSON} feature
 *
 */
export function WhatsHereShowGeom (fc) {
  const sourceWhatsHere = PoziUtils.getOpenLayersObjectFromTitleID(WHATS_HERE_LAYER_TITLEID).getSource()
  if (isNil(sourceWhatsHere)) {
    return
  }
  if (isNil(fc)) {
    return
  }

  let fcHasGeom = false

  // Simple way to check if feature (collection) has any geometries
  if (fc.type === 'FeatureCollection') {
    const features = fc.features || []
    fcHasGeom = features.some(feature => feature.geometry)
  } else {
    fcHasGeom = !!fc.geometry
  }

  // console.debug('WhatsHereUtils - WhatsHereShowGeom - feature collection has geom?', fcHasGeom)

  // Remove the current Whats Here
  sourceWhatsHere.clear(true)

  if (!fcHasGeom) {
    return
  }

  const olFeatureArr = PoziUtils.convertGeoJSONFeatureCollectionToOLFeatureArray(fc) || []
  if (!olFeatureArr?.length) {
    return
  }

  // console.debug('WhatsHereShowGeom', fc, [...olFeatureArr])

  // Strip features of all attributes to avoid them having interfering styles
  const olFeatureArrClean = olFeatureArr.map(feature => {
    const cleanFeature = new OLFeature()
    cleanFeature.setGeometry(feature.getGeometry())
    return cleanFeature
  })

  sourceWhatsHere.addFeatures(olFeatureArrClean)
  // sourceWhatsHere.refresh()
}

/**
 * Select WhatsHere from featureCollectionT (T = TurfJS)
 *
 * @param {geojson} featureCollectionT
 *
 */
export async function ShowWhatsHere (featureCollectionT, bufferMeters = -0.1) {
  PoziMap().setMapDefaults() // Model() events will fail to fire if the defaults are not set first.
  WhatsHereClear()

  await PoziUtils.sleep(10) // allow WhatsHereClear to complete (yes, it's a hack)

  console.debug('Show whats here...', featureCollectionT)

  const activeTabId = Model.get('app.drawer.activetabid', null)
  if (activeTabId !== 'info') {
    Model.set('app.drawer.activetabid', 'info')
  }

  const olLayer = PoziUtils.getOpenLayersObjectFromTitleID(WHATS_HERE_LAYER_TITLEID)
  if (isNil(featureCollectionT) || isNil(featureCollectionT.features)) {
    console.log('Invalid feature collection!')
    return
  }

  let bufferedFC = featureCollectionT
  const geomTypeOrig = featureCollectionT?.features?.[0]?.geometry?.type || ''

  // Simplify geometry types to either 'line', 'point' or 'polygon'
  const geomTypeSimple = PoziUtils.getGeomTypeSimple(geomTypeOrig)

  // Default buffer for polygons is -0.1m to avoid selecting surrounding stuff
  if (geomTypeSimple === 'polygon') {
    bufferedFC = buffer(featureCollectionT, bufferMeters, { units: 'meters' })
    // If the negative buffer deletes the feature, then warn the user.
    if (bufferedFC.features.length === 0) {
      PoziNotifications().warning('Negative buffer is too large and has removed the selected feature')
      return
    }
  } else if (bufferMeters > 0) {
    bufferedFC = buffer(featureCollectionT, bufferMeters, { units: 'meters' })
  }

  // Get all select features (by line/polygon and (ctrl-)click) from model
  let mergedFeatureCollection = GetControlKeyMergedFeatureCollection(bufferedFC)

  const fcOriginalIsPointsOnly = featureCollectionIsPointsOnly(mergedFeatureCollection)
  // console.debug('ShowWhatsHere - fcOriginalIsPointsOnly', fcOriginalIsPointsOnly)

  let olFeatureArr = []

  if (!fcOriginalIsPointsOnly) {
    // Convert all features to to polygons
    const mergedFeatureCollectionPolygons = convertFeatureCollectionFeaturesToPolygons(mergedFeatureCollection)
    // console.debug('ShowWhatsHere - mergedFeatureCollectionPolygons', mergedFeatureCollectionPolygons)

    mergedFeatureCollection = mergedFeatureCollectionPolygons
  }

  let fcCombined
  // console.debug('ShowWhatsHere - feature collection number of features?', mergedFeatureCollection.features?.length, mergedFeatureCollection)

  if (mergedFeatureCollection.features?.length > 1) {
    // console.debug('ShowWhatsHere - more than 1 feature in merged feature collection. Combining...')

    // Combine all features into one multi-polygon
    fcCombined = combine(mergedFeatureCollection)
  } else {
    fcCombined = mergedFeatureCollection
  }

  // console.debug('ShowWhatsHere - fcCombined', cloneDeep(fcCombined))

  // Show the current target layer
  currentTargetLayerShowGeom(fcCombined)

  // Convert all GeoJSON features to OL features
  olFeatureArr = PoziUtils.convertGeoJSONFeatureCollectionToOLFeatureArray(fcCombined)

  // console.debug('ShowWhatsHere - olFeatureArr', olFeatureArr)

  // Select the first feature (there should be only one max!)
  if (olFeatureArr.length > 0) {
    // Sanity check. Due to the polygon conversion + combine, we should never get here!
    if (olFeatureArr.length > 1) {
      console.log('Warning - Merged feature collection has more than one feature, using first feature for whatshere. This can happen when merging multiple geom types... point/polygon/etc')
    }

    const firstOlFeature = olFeatureArr[0]

    console.debug('ShowWhatsHere - based on first feature from combined features', firstOlFeature)

    selectVectorFeature(firstOlFeature, olLayer, false)
  } else {
    console.log('ShowWhatsHere - invalid merged feature collection!', fcCombined, olFeatureArr)
  }
}

const GetControlKeyMergedFeatureCollection = function (featureCollectionT) {
  let fc = featureCollectionT
  const bControlKey = Model.get('app.map.click.controlkey', false)
  if (bControlKey) {
    // fc = convertPointsToPolygons(featureCollectionT)

    const controlkeyfc = Model.get('app.map.click.controlkeyfc', null)
    // controlkeyfc = convertPointsToPolygons(controlkeyfc)

    if (controlkeyfc && controlkeyfc.features) {
      fc = featureCollection([...fc.features, ...controlkeyfc.features])
    }
    Model.set('app.map.click.controlkeyfc', fc)
  } else {
    Model.set('app.map.click.controlkeyfc', fc)
  }
  Model.set('app.map.click.controlkey', false)

  // console.debug('WhatsHereUtils - GetControlKeyMergedFeatureCollection', fc)

  return fc
}

export function UseWhatsHereInfoPanel () {
  const modes = Model.get('config.modes', [])
  if (modes.length === 0) {
    console.log('No modes')
    return true
  }
  return false
}

export function ShowWhatsHereFeatureFromURL () {
  const featureObj = Model.getState('feature', null)

  // console.debug('ShowWhatsHereFeatureFromURL - featureObj', featureObj)

  if (isNil(featureObj)) {
    return
  }
  const titleid = PoziUtils.getNameOfFirstKey(featureObj)
  if (titleid !== WHATS_HERE_LAYER_TITLEID) {
    return
  }

  let wktValue = get(featureObj, '[' + titleid + ']', null)

  // It could be that the value is just a simple coordinate. If so, convert it to a WKT point
  if (wktValue) {
    const simpleCoordinateValue = wktValue.match(/^[\d-. ]+$/)?.[0]
    if (simpleCoordinateValue) {
      wktValue = `POINT(${simpleCoordinateValue})`
    }
  }

  try {
    const fc = WKTToGeoJSON(wktValue)

    // console.debug('ShowWhatsHereFeatureFromURL - fc', fc)

    ShowWhatsHere(fc)
  } catch (err) {
    console.log('ShowWhatsHereFeatureFromURL - failed to parse wkt object.')
  }
}

export function GetWhatsHereWKT () {
  const featureObj = Model.getState('feature', null)

  // console.debug('GetWhatsHereWKT', featureObj)

  if (isNil(featureObj)) {
    return null
  }
  const titleid = PoziUtils.getNameOfFirstKey(featureObj)
  if (titleid !== WHATS_HERE_LAYER_TITLEID) {
    return null
  }
  const wkt = get(featureObj, '[' + titleid + ']', null)
  return wkt
}
